import { tokens } from '@fluentui/react-components';
import type { EventStatus } from '@/types/calendarTypes';

/**
 * Status colors used by calendar event chips and room booking blocks.
 * Mirrors the StatusTag mapping (warning / confirmed / danger / cancelled / draft)
 * but exposed as plain { bg, border, text } strings because the generic
 * Calendar primitives consume them as inline style values, not className tokens.
 *
 * Values are Fluent UI v9 token CSS custom-property references, so palette
 * changes flow through automatically via the active theme.
 */
const STATUS_COLORS: Record<EventStatus, { bg: string; border: string; text: string }> = {
  draft: {
    bg: tokens.colorNeutralBackground1Selected,
    border: tokens.colorNeutralStroke1,
    text: tokens.colorNeutralForeground1,
  },
  submitted: {
    bg: `var(--gem-status-new-request-background, ${tokens.colorPaletteYellowBackground2})`,
    border: `var(--gem-status-new-request-border, ${tokens.colorPaletteYellowBorder2})`,
    text: `var(--gem-status-new-request-foreground, ${tokens.colorPaletteRedForeground2})`,
  },
  under_review: {
    bg: `var(--gem-status-under-review-background, ${tokens.colorPaletteMarigoldBackground2})`,
    border: `var(--gem-status-under-review-border, ${tokens.colorPaletteMarigoldBorder2})`,
    text: `var(--gem-status-under-review-foreground, ${tokens.colorPaletteRedForeground2})`,
  },
  request_for_cancellation: {
    bg: tokens.colorPaletteDarkOrangeBackground2,
    border: tokens.colorPaletteDarkOrangeBorder2,
    text: tokens.colorPaletteDarkOrangeForeground2,
  },
  confirmed: {
    bg: tokens.colorPaletteLightGreenBackground2,
    border: tokens.colorPaletteLightGreenBorder2,
    text: `var(--gem-calendar-confirmed-foreground, ${tokens.colorPaletteGreenForeground2})`,
  },
  completed: {
    bg: tokens.colorNeutralBackground3,
    border: tokens.colorNeutralStroke2,
    text: tokens.colorNeutralForeground2,
  },
  declined: {
    bg: tokens.colorPaletteRedBackground2,
    border: tokens.colorPaletteRedBorder2,
    text: tokens.colorPaletteRedForeground2,
  },
  did_not_occur: {
    bg: tokens.colorNeutralBackground3,
    border: tokens.colorNeutralStroke2,
    text: tokens.colorNeutralForeground2,
  },
  cancelled: {
    bg: tokens.colorPaletteCranberryBackground2,
    border: tokens.colorPaletteCranberryBorderActive,
    text: tokens.colorPaletteCranberryForeground2,
  },
};

export function getStatusStyle(status: EventStatus) {
  return STATUS_COLORS[status] ?? STATUS_COLORS.draft;
}
